/**
 * <gift-progress>
 *
 * Free gift progress bar for the product page. Server-rendered by
 * snippets/cart-gift-notification.liquid (context: 'product'), then hydrated and kept
 * in sync entirely client-side. Cart page and drawer use section bundling instead.
 *
 * Consumes events already dispatched on `document` by theme code:
 *
 *   - 'ajaxProduct:added'  assets/component-product-form.js. detail may be an unawaited
 *                          Promise, so we always re-fetch the cart on this event.
 *   - 'cart:update'        assets/cart.js. detail is the /cart/change.js JSON response
 *                          and carries total_price + item_count — no fetch needed.
 *
 * Currency: data-threshold and cart.total_price are both shop-currency cents. Only the
 * displayed remaining amount is converted via Woolman.Utils.formatMoney, which applies
 * Shopify.currency.rate internally — never pre-multiply here.
 */
(function () {
  if (customElements.get('gift-progress')) return;

  // Shared across instances so multiple bars on one page make only one request.
  let inFlight = null;

  function fetchCart() {
    if (inFlight) return inFlight;

    const base = (window.routes && window.routes.cart_url) || '/cart';

    inFlight = fetch(`${base}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then((res) => res.json())
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  }

  customElements.define('gift-progress', class GiftProgress extends HTMLElement {
    connectedCallback() {
      this.messageEl = this.querySelector('[data-gift-progress-message]');
      this.statusEl  = this.querySelector('[data-gift-progress-status]');
      this.barEl     = this.querySelector('[data-gift-progress-bar]');
      this.fillEl    = this.querySelector('[data-gift-progress-fill]');

      this.threshold = parseInt(this.dataset.threshold, 10);
      if (!this.threshold || this.threshold <= 0) return;

      this.lastAnnounced = this.statusEl ? this.statusEl.textContent.trim() : '';

      this.onCartChange = this.onCartChange.bind(this);
      this.onPageShow   = this.onPageShow.bind(this);

      document.addEventListener('ajaxProduct:added', this.onCartChange);
      document.addEventListener('cart:update', this.onCartChange);
      window.addEventListener('pageshow', this.onPageShow);

      // Re-read on mount: a full-page cache may have served stale Liquid cart data.
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('ajaxProduct:added', this.onCartChange);
      document.removeEventListener('cart:update', this.onCartChange);
      window.removeEventListener('pageshow', this.onPageShow);
      clearTimeout(this.announceTimer);
    }

    onPageShow(event) {
      if (event.persisted) this.refresh();
    }

    onCartChange(event) {
      const detail = event && event.detail;

      // cart:update detail is the parsed /cart/change.js JSON (integer total_price).
      // ajaxProduct:added detail may be an unawaited Promise — skip and fetch instead.
      if (detail && Number.isInteger(detail.total_price)) {
        this.render(detail.total_price, detail.item_count);
        return;
      }

      this.refresh();
    }

    refresh() {
      return fetchCart()
        .then((cart) => this.render(cart.total_price, cart.item_count))
        .catch((err) => {
          console.error('[gift-progress] Could not read the cart.', err);
        });
    }

    render(totalPrice, itemCount) {
      const total     = Math.max(0, parseInt(totalPrice, 10) || 0);
      const remaining = Math.max(0, this.threshold - total);
      const percent   = Math.min(100, Math.floor((total / this.threshold) * 100));
      const unlocked  = remaining === 0;

      const strings = window.cartStrings || {};
      const message = unlocked
        ? (strings.giftFree || '')
        : (strings.giftNotFree || '').replace('[amount]', window.Woolman.Utils.formatMoney(remaining));

      this.fillEl.style.setProperty('--gift-progress', `${percent}%`);
      this.barEl.setAttribute('aria-valuenow', String(percent));
      this.barEl.setAttribute('aria-valuetext', message);
      this.classList.toggle('gift-progress--unlocked', unlocked);
      this.messageEl.textContent = message;

      this.announce(message);
    }

    /**
     * Trailing-debounced and only fires when the sentence changed, so a burst of quantity
     * clicks yields at most one announcement.
     */
    announce(message) {
      if (!this.statusEl || message === this.lastAnnounced) return;

      this.lastAnnounced = message;
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {
        this.statusEl.textContent = message;
      }, 500);
    }
  });
})();
